import Modal from 'ds/components/modal';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { IconWrapper, Info, Inputs, InputsWrapper, LikeInput, MoreResultsWrapper, SuggestedList, SuggestedListItemWrapper, Wrapper } from './styled';
import AutocompleteInput from 'components/autocomplete/single-location-autocomplete';
import { useLocale } from 'locale';
import { CompletionType, IAutocompleteEntry, IAutocompleteNeighbourhood } from 'utils/entities';
import CompareIcon from 'assets/svg/compare-inputs.svg';
import CityIcon from 'assets/svg/city-bw.svg';
import { State } from 'store/state';
import { isInvestorsCompareModalOpenSelector } from 'store/state/app/selectors';
import { navigateTo } from 'store/state/router/actions';
import { NavigateToFn, Route } from 'config/routes';
import { ICompareModalProps } from './types';
import { setInvestorsCompareModalAction, setInvestorsRecentSearchIdAction } from 'store/state/app/actions';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { investorRecentSearchesSelector, similarNeighbourhoodInvestorListSelector } from 'store/state/domainData/selectors/investors';
import { INeighborhoodSummaryDocument } from 'store/sagas/apiService/types';
import { SmallText } from 'ds/components/typography';
import { SpacingBlock } from 'components/spacing-block';
import { SimilarHoodItem } from './SimilarHoodItem';
import { debounce } from 'lodash';


const InvestorsCompareModalBase: React.FC<{
  baseInfo: ICompareModalProps;
  navigate: NavigateToFn;
  setModalOpen: (data: ICompareModalProps) => void;
  similarNeighbourhoods: INeighborhoodSummaryDocument[];
  searchHistory: Array<{
    doc: {
      docId: string;
      ref?: IAutocompleteNeighbourhood;
    };
  }>;
  saveSearch: typeof setInvestorsRecentSearchIdAction,
}> = ({ baseInfo, navigate, setModalOpen, similarNeighbourhoods, searchHistory, saveSearch }) => {
  const { t } = useLocale();
  const [ searchStr, setSearchStr ] = useState('');
  const [ hideList, setHideList ] = useState(false);
  const isDesktop = useScreenBreakpoint() >= 3;

  const handleNavigation = (compareId: string) => {
    setSearchStr('');
    handleCloseModal();
    navigate(Route.InvestorsNeighbourhoodPage, { id: baseInfo.id, compareId });
  };

  useEffect(() => {
    if (!searchStr) {
      debounce(() => setHideList(false), 500)();
    }
    else {
      setHideList(true);
    }
  }, [ searchStr, setHideList ]);

  const onAutocompleteInputChange = (value: IAutocompleteEntry) => {
    if (value && value.type.toLowerCase() === CompletionType.Neighbourhood) {
      handleNavigation(value.docId);
      saveSearch(value.docId);
    }
  };

  const handleSuggestedClick = (compareId: string) => {
    handleNavigation(compareId);
  };

  if (!baseInfo) return null;

  const handleCloseModal = () => {
    setModalOpen(null);
  };

  return (
    <Modal
      isOpen
      onClose={handleCloseModal}
      title={'בחרו שכונה להשוואה'}
      titleStyle={isDesktop ? { borderBottom: '1px solid #ececec', height: 58, fontSize: 13, marginBottom: 24 } : null}
      isFullWidthMainContent
      contentWrapperStyle={isDesktop ? { padding: 0, minWidth: 576, minHeight: 674 } : null}
      desktopModalPositionCenter
      closeButtonStyle={isDesktop ? { top: 16, left: 16 } : null}
    >
      <Wrapper>
        <InputsWrapper>
          <CompareIcon />
          <Inputs>
            <LikeInput>
              <div style={{ width: '100%' }}>
                {baseInfo.name}, {baseInfo.city}
              </div>
            </LikeInput>
            <AutocompleteInput
              searchStr={searchStr}
              placeholder={t('investors.autocomplete.placeholder')}
              inputIcon={null}
              completionTypes={[ CompletionType.Neighbourhood ]}
              optionsCount={6}
              onSearchStrChange={setSearchStr}
              onChange={onAutocompleteInputChange}
              suggestionItemIcon={<CityIcon width={24} height={24} />}
            />
          </Inputs>
        </InputsWrapper>
        {hideList ? null : (
          <>
            <SpacingBlock mTop={5} />
            <MoreResultsWrapper>
              {similarNeighbourhoods && similarNeighbourhoods.length ? (
                <>
                  <SmallText>{t('investors.suggested.neightbourhoods.comapre.title', { name: baseInfo.name, city: baseInfo.city })}</SmallText>
                  <SpacingBlock mTop={2} />
                  <SuggestedList>
                    {similarNeighbourhoods.map((el, key) => el.neighborhoodDoc.docId !== baseInfo.id ? (
                      <SimilarHoodItem
                        key={key}
                        onClick={() => handleSuggestedClick(el.neighborhoodDoc.docId)}
                        name={el.neighborhoodDoc.ref.name}
                        labels={el.investorCard.labels}
                      />
                    ) : null).filter(Boolean)}
                  </SuggestedList>
                </>
              ) : null}
              {searchHistory && searchHistory.length ? (
                <>
                  <SpacingBlock mTop={5} />
                  <SmallText>{t('investors.recentSearch.neighbourhoods.title.only')}</SmallText>
                  <SpacingBlock mTop={2} />
                  <SuggestedList>
                    {searchHistory.map((el, key) => (
                      <SuggestedListItemWrapper key={key} onClick={() => handleSuggestedClick(el.doc.docId)}>
                        <IconWrapper skipWrapper>
                          <CityIcon width={24} height={24} />
                        </IconWrapper>
                        <Info>
                          <SmallText>{el.doc.ref.name}</SmallText>
                        </Info>
                      </SuggestedListItemWrapper>
                    ))}
                  </SuggestedList>
                </>
              ) : null}
              <SpacingBlock mTop={5} />
            </MoreResultsWrapper>
          </>
        )}
      </Wrapper>
    </Modal>
  );
};

const mapStateToProps = (state: State) => ({
  baseInfo: isInvestorsCompareModalOpenSelector(state),
  similarNeighbourhoods: similarNeighbourhoodInvestorListSelector(state),
  searchHistory: investorRecentSearchesSelector(state),
});

const mapDispatchToProps = {
  navigate: navigateTo,
  setModalOpen: setInvestorsCompareModalAction,
  saveSearch: setInvestorsRecentSearchIdAction,
};

export const InvestorsCompareModal = connect(mapStateToProps, mapDispatchToProps)(InvestorsCompareModalBase);
